import { useMemo } from "react";

import type { ClosingTime, ClosingTimeType } from "@luna-restaurant/school-api";

import { useCollection } from "@/hooks/useCollection";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { getBerlinTodayDateOnlyOrEarlier } from "@/utils/closingTimes/berlinDateOnly";

type UseClosingTimesOptions = {
  type?: ClosingTimeType;
  forSchoolClass?: boolean;
  schoolClassIri?: string;
  itemsPerPage?: number;
  fetchAllPages?: boolean;
  enabled?: boolean;
};

export const useClosingTimes = (options?: UseClosingTimesOptions) => {
  const { currentUser } = useCurrentUser();
  const berlinToday = getBerlinTodayDateOnlyOrEarlier();

  const query = useMemo(() => {
    const params: Record<string, string> = {
      "effectiveAt[from]": berlinToday,
    };
    if (options?.type) {
      params.type = options.type;
    }
    if (options?.schoolClassIri) {
      params.schoolClass = options.schoolClassIri;
    } else if (typeof options?.forSchoolClass === "boolean") {
      params["exists[schoolClass]"] = String(options.forSchoolClass);
    }
    if (typeof options?.itemsPerPage === "number") {
      params.itemsPerPage = String(options.itemsPerPage);
    }
    return params;
  }, [
    berlinToday,
    options?.type,
    options?.forSchoolClass,
    options?.itemsPerPage,
    options?.schoolClassIri,
  ]);

  return useCollection<ClosingTime>({
    queryKey: ["closing-times", currentUser?.school],
    path: currentUser?.school ? `${currentUser.school}/closing-times` : "",
    query,
    enabled: !!currentUser?.school && (options?.enabled ?? true),
    fetchAllPages: options?.fetchAllPages,
  });
};
